const PUBLIC_SITE_URL = 'https://spothr.net';
const DEFAULT_SOCIAL_IMAGE = `${PUBLIC_SITE_URL}/post.jpg`;
const STRUCTURED_DATA_ID = 'spothr-structured-data';

type FaqItem = {
  question: string;
  answer: string;
};

export interface SeoMetadata {
  title: string;
  description: string;
  canonicalUrl: string;
  robots: string;
  structuredData?: Record<string, unknown>;
  // Optional absolute URL for the og:image / twitter:image. When omitted, the
  // branded DEFAULT_SOCIAL_IMAGE is used. This lets individual routes ship a
  // page-specific social card once dedicated artwork exists, without changing
  // any call sites.
  socialImage?: string;
}

const organizationStructuredData = {
  '@type': 'Organization',
  name: 'SpotHR',
  url: PUBLIC_SITE_URL,
  logo: `${PUBLIC_SITE_URL}/logo512.png`
};

const softwareApplicationStructuredData = {
  '@type': 'SoftwareApplication',
  name: 'SpotHR',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Web',
  offers: {
    '@type': 'AggregateOffer',
    lowPrice: '24.99',
    highPrice: '99.99',
    priceCurrency: 'USD'
  },
  description:
    'SpotHR helps small teams manage leave, onboarding, expense claims, and core HR workflows in one place.',
  url: PUBLIC_SITE_URL
};

const buildFaqStructuredData = (faqItems: FaqItem[]) => ({
  '@type': 'FAQPage',
  mainEntity: faqItems.map(item => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer
    }
  }))
});

const buildStructuredData = (faqItems?: FaqItem[]) => ({
  '@context': 'https://schema.org',
  '@graph': [
    organizationStructuredData,
    softwareApplicationStructuredData,
    ...(faqItems?.length ? [buildFaqStructuredData(faqItems)] : [])
  ]
});

const publicSeoPages: Record<string, SeoMetadata> = {
  '/': {
    title: 'HR software for small teams and startups | SpotHR',
    description:
      'SpotHR gives small teams one place for leave management, expense claims, onboarding, and practical HR workflows.',
    canonicalUrl: PUBLIC_SITE_URL,
    robots: 'index, follow',
    structuredData: buildStructuredData()
  },
  '/pricing': {
    title: 'Simple HR pricing for startups and small teams | SpotHR',
    description:
      'See straightforward SpotHR pricing for small teams, startups, and scale-ups with full-organisation plans and a 30-day free trial.',
    canonicalUrl: `${PUBLIC_SITE_URL}/pricing`,
    robots: 'index, follow',
    structuredData: buildStructuredData([
      {
        question: 'How is SpotHR priced?',
        answer:
          'SpotHR is priced per organisation, not per seat, with separate plans for small teams, startups, and scale-ups.'
      },
      {
        question: 'Can we try SpotHR before paying?',
        answer:
          'Yes. Every pricing tier starts with a 30-day free trial so teams can test their HR workflows before subscribing.'
      }
    ])
  },
  '/signup': {
    title: 'Simple HR pricing for startups and small teams | SpotHR',
    description:
      'See straightforward SpotHR pricing for small teams, startups, and scale-ups with full-organisation plans and a 30-day free trial.',
    canonicalUrl: `${PUBLIC_SITE_URL}/pricing`,
    robots: 'index, follow',
    structuredData: buildStructuredData([
      {
        question: 'How is SpotHR priced?',
        answer:
          'SpotHR is priced per organisation, not per seat, with separate plans for small teams, startups, and scale-ups.'
      },
      {
        question: 'Can we try SpotHR before paying?',
        answer:
          'Yes. Every pricing tier starts with a 30-day free trial so teams can test their HR workflows before subscribing.'
      }
    ])
  },
  '/features': {
    title: 'HR software features for leave, expenses, and onboarding | SpotHR',
    description:
      'Explore SpotHR features for leave requests, expense claim workflows, onboarding, org structure, and startup-friendly HR operations.',
    canonicalUrl: `${PUBLIC_SITE_URL}/features`,
    robots: 'index, follow',
    structuredData: buildStructuredData([
      {
        question: 'Which HR workflows does SpotHR support?',
        answer:
          'SpotHR covers leave management, expense claims, onboarding, org administration, public holidays, and employee self-service workflows.'
      },
      {
        question: 'Who is SpotHR built for?',
        answer:
          'SpotHR is designed for startups, small businesses, and growing teams that want practical HR software without enterprise complexity.'
      }
    ])
  },
  '/features/leave-management': {
    title: 'Simple leave management for small teams | SpotHR',
    description:
      'Manage leave requests, approvals, balances, public holidays, and sick leave in one lightweight workflow built for growing teams.',
    canonicalUrl: `${PUBLIC_SITE_URL}/features/leave-management`,
    robots: 'index, follow',
    structuredData: buildStructuredData()
  },
  '/features/expense-claims': {
    title: 'Expense claim management for startups | SpotHR',
    description:
      'Run faster expense claim workflows with receipt uploads, approval steps, line items, travel reimbursements, and shared claim detail pages.',
    canonicalUrl: `${PUBLIC_SITE_URL}/features/expense-claims`,
    robots: 'index, follow',
    structuredData: buildStructuredData()
  },
  '/features/onboarding': {
    title: 'Employee onboarding tracking for growing teams | SpotHR',
    description:
      'Track onboarding tasks, required documents, and ownership so new hires can get up to speed without extra admin overhead.',
    canonicalUrl: `${PUBLIC_SITE_URL}/features/onboarding`,
    robots: 'index, follow',
    structuredData: buildStructuredData()
  },
  '/compare/personio': {
    title: 'SpotHR vs Personio for small teams and startups',
    description:
      'Compare SpotHR with Personio for small teams that want simpler setup, clearer pricing, and operational HR workflows without enterprise overhead.',
    canonicalUrl: `${PUBLIC_SITE_URL}/compare/personio`,
    robots: 'index, follow',
    structuredData: buildStructuredData()
  },
  '/compare/bamboohr': {
    title: 'SpotHR vs BambooHR for practical startup HR',
    description:
      'See how SpotHR compares with BambooHR for leave, onboarding, and expense workflows that growing teams want to keep lightweight and easy to adopt.',
    canonicalUrl: `${PUBLIC_SITE_URL}/compare/bamboohr`,
    robots: 'index, follow',
    structuredData: buildStructuredData()
  },
  '/compare/spreadsheets': {
    title: 'SpotHR vs spreadsheets for leave and HR operations',
    description:
      'Replace spreadsheet-based leave, onboarding, and expense tracking with SpotHR when your team needs cleaner workflows and clearer ownership.',
    canonicalUrl: `${PUBLIC_SITE_URL}/compare/spreadsheets`,
    robots: 'index, follow',
    structuredData: buildStructuredData()
  },
  '/startup-hr-software': {
    title: 'Startup HR software for lean people operations | SpotHR',
    description:
      'SpotHR gives startups one place for leave, onboarding, expense claims, and operational HR workflows without enterprise setup complexity.',
    canonicalUrl: `${PUBLIC_SITE_URL}/startup-hr-software`,
    robots: 'index, follow',
    structuredData: buildStructuredData()
  },
  '/leave-management-for-small-teams': {
    title: 'Leave management for small teams | SpotHR',
    description:
      'Run leave requests, approvals, balances, and holiday planning in one lightweight workflow built for founders, managers, and small HR teams.',
    canonicalUrl: `${PUBLIC_SITE_URL}/leave-management-for-small-teams`,
    robots: 'index, follow',
    structuredData: buildStructuredData()
  },
  '/expense-claims-for-startups': {
    title: 'Expense claims for startups without extra admin | SpotHR',
    description:
      'SpotHR keeps startup expense claims practical with draft claims, receipt uploads, approval steps, and clear status tracking in one workflow.',
    canonicalUrl: `${PUBLIC_SITE_URL}/expense-claims-for-startups`,
    robots: 'index, follow',
    structuredData: buildStructuredData()
  },
  '/employee-onboarding-software': {
    title: 'Employee onboarding software for growing teams | SpotHR',
    description:
      'Track onboarding checklists, required documents, and ownership in SpotHR so new hires move forward without spreadsheet follow-up.',
    canonicalUrl: `${PUBLIC_SITE_URL}/employee-onboarding-software`,
    robots: 'index, follow',
    structuredData: buildStructuredData()
  },
  '/mcp-setup': {
    title: 'Connect Spot HR to your AI assistant (MCP) | SpotHR',
    description:
      'Connect Spot HR to Claude and other AI assistants with the built-in MCP server. Check leave, submit requests, and review expenses through a secure OAuth sign-in.',
    canonicalUrl: `${PUBLIC_SITE_URL}/mcp-setup`,
    robots: 'index, follow',
    structuredData: buildStructuredData()
  }
};

const normalizePath = (pathname: string) => {
  if (!pathname || pathname === '/') {
    return '/';
  }

  const pathWithLeadingSlash = pathname.startsWith('/')
    ? pathname
    : `/${pathname}`;

  return pathWithLeadingSlash.replace(/\/+$/, '') || '/';
};

const getFallbackCanonicalUrl = (pathname: string) =>
  pathname === '/' ? PUBLIC_SITE_URL : `${PUBLIC_SITE_URL}${pathname}`;

export const resolveSeoMetadata = (
  pathname: string,
  fallbackTitle?: string
): SeoMetadata => {
  const normalizedPath = normalizePath(pathname);
  const publicPage = publicSeoPages[normalizedPath];

  if (publicPage) {
    return publicPage;
  }

  return {
    title: fallbackTitle ? `Spot HR - ${fallbackTitle}` : 'Spot HR',
    description: 'SpotHR helps teams manage practical HR workflows.',
    canonicalUrl: getFallbackCanonicalUrl(normalizedPath),
    robots: 'noindex, nofollow'
  };
};

const upsertMetaContentTag = (
  selector: string,
  attribute: 'name' | 'property',
  key: string,
  value: string
) => {
  let element = document.head.querySelector(selector) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.setAttribute('content', value);
};

const upsertLinkTag = (selector: string, rel: string, href: string) => {
  let element = document.head.querySelector(selector) as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }

  element.setAttribute('href', href);
};

const getStructuredDataElement = () => {
  const existingElement = document.getElementById(STRUCTURED_DATA_ID);
  if (existingElement) {
    return existingElement;
  }

  const element = document.createElement('script');
  element.id = STRUCTURED_DATA_ID;
  element.type = 'application/ld+json';
  document.head.appendChild(element);
  return element;
};

export const applySeoMetadata = (metadata: SeoMetadata) => {
  const socialImage = metadata.socialImage || DEFAULT_SOCIAL_IMAGE;
  document.title = metadata.title;
  upsertMetaContentTag(
    'meta[name="description"]',
    'name',
    'description',
    metadata.description
  );
  upsertMetaContentTag(
    'meta[name="robots"]',
    'name',
    'robots',
    metadata.robots
  );
  upsertMetaContentTag(
    'meta[name="googlebot"]',
    'name',
    'googlebot',
    metadata.robots
  );
  upsertMetaContentTag(
    'meta[name="bingbot"]',
    'name',
    'bingbot',
    metadata.robots
  );
  upsertMetaContentTag(
    'meta[property="og:title"]',
    'property',
    'og:title',
    metadata.title
  );
  upsertMetaContentTag(
    'meta[property="og:description"]',
    'property',
    'og:description',
    metadata.description
  );
  upsertMetaContentTag(
    'meta[property="og:type"]',
    'property',
    'og:type',
    'website'
  );
  upsertMetaContentTag(
    'meta[property="og:url"]',
    'property',
    'og:url',
    metadata.canonicalUrl
  );
  upsertMetaContentTag(
    'meta[property="og:image"]',
    'property',
    'og:image',
    socialImage
  );
  upsertMetaContentTag(
    'meta[property="og:site_name"]',
    'property',
    'og:site_name',
    'SpotHR'
  );
  upsertMetaContentTag(
    'meta[name="twitter:card"]',
    'name',
    'twitter:card',
    'summary_large_image'
  );
  upsertMetaContentTag(
    'meta[name="twitter:title"]',
    'name',
    'twitter:title',
    metadata.title
  );
  upsertMetaContentTag(
    'meta[name="twitter:description"]',
    'name',
    'twitter:description',
    metadata.description
  );
  upsertMetaContentTag(
    'meta[name="twitter:image"]',
    'name',
    'twitter:image',
    socialImage
  );
  upsertLinkTag('link[rel="canonical"]', 'canonical', metadata.canonicalUrl);

  const existingStructuredData = document.getElementById(STRUCTURED_DATA_ID);
  if (metadata.structuredData) {
    const structuredDataElement = getStructuredDataElement();
    structuredDataElement.textContent = JSON.stringify(metadata.structuredData);
  } else {
    existingStructuredData?.remove();
  }
};

export const SEO_PUBLIC_PATHS = Object.keys(publicSeoPages);
export { PUBLIC_SITE_URL };

// Expose the public marketing route list so the build-time prerenderer
// (scripts/prerender.js) can derive the exact set of pages to snapshot from the
// running app, keeping SEO_PUBLIC_PATHS the single source of truth.
if (typeof window !== 'undefined') {
  (window as unknown as {
    __SEO_PUBLIC_PATHS__?: string[];
  }).__SEO_PUBLIC_PATHS__ = SEO_PUBLIC_PATHS;
}
